import { isObject, type Port, PortTypes, useApiAccountsV1PortsList, useApiAccountsV1PortsRetrieve } from 'api';
import { useMemo, useState } from 'react';

import type { WithOptional } from '../../utils/types';
import { AutocompleteField, type AutocompleteFieldProps } from '../AutocompleteField/AutocompleteField';
import { getCountryNameByCountryAbbr } from '../CountryAutocompleteField/countries';

type Props = Omit<WithOptional<AutocompleteFieldProps<Port>, 'label'>, 'options' | 'defaultValue'> & {
  type?: PortTypes;
  defaultValue?: Port['id'];
};

const getPortTypeDisplayedName = (portType: PortTypes | undefined) => {
  switch (portType) {
    case PortTypes.ICD:
      return 'ICD';
    case PortTypes.REG:
      return 'Port';
    default:
      return 'Port';
  }
};

export const getPortDisplayedName = (port: Port) =>
  `${getPortTypeDisplayedName(port.type)} ${port.name} in ${getCountryNameByCountryAbbr(port.country || 'GB')} [${port.unlocode}]`;

export const PortAutocomplete = ({
  name,
  label = 'Select port',
  required = false,
  disabled = false,
  type = undefined,
  defaultValue,
  ...rest
}: Props) => {
  const [query, setQuery] = useState('');

  const { data: listData, isLoading: isListLoading } = useApiAccountsV1PortsList({
    search: query,
    type: type,
  });

  const { data: defaultPort, isLoading: isDefaultLoading } = useApiAccountsV1PortsRetrieve(defaultValue ?? 0, {
    query: {
      enabled: Boolean(defaultValue),
    },
  });

  const options = useMemo(() => {
    const results = listData?.results || [];

    // If we have a default port and it's not already in the results, add it
    if (defaultPort && !results.some((port) => port.id === defaultPort.id)) {
      return [defaultPort, ...results];
    }

    return results;
  }, [listData?.results, defaultPort]);

  return (
    <AutocompleteField<Port>
      {...rest}
      options={options}
      loading={isListLoading || isDefaultLoading}
      name={name}
      label={label}
      valueField="id"
      required={required}
      disabled={disabled}
      getOptionLabel={(item) => {
        if (isObject(item)) {
          return getPortDisplayedName(item);
        }
        return '';
      }}
      filterOptions={(options, state) => {
        setQuery(state.inputValue);
        return options;
      }}
    />
  );
};
